import { useState, useEffect, useMemo } from 'react'
import {
  Box,
  Grid,
  Paper,
  Typography,
  IconButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  Divider,
  Tooltip,
  Badge,
  CircularProgress,
} from '@mui/material'
import {
  ChevronLeft,
  ChevronRight,
  Today,
  Build,
  EventRepeat,
  Speed,
  Warning,
  CheckCircle,
  Schedule,
  Person,
  FilterList,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { PageHeader, Card, StatusChip } from '@components/common'
import { formatDate } from '@utils/dateUtils'
import { toast } from 'react-toastify'
import api from '@services/api'
import { useAuth } from '@context/AuthContext'

// Activity type icons and colors
const ACTIVITY_CONFIG = {
  work_order: {
    icon: <Build sx={{ fontSize: 16 }} />,
    label: 'Work Order',
    color: '#2196F3',
  },
  pm: {
    icon: <EventRepeat sx={{ fontSize: 16 }} />,
    label: 'PM',
    color: '#3F51B5',
  },
  calibration: {
    icon: <Speed sx={{ fontSize: 16 }} />,
    label: 'Calibration',
    color: '#9C27B0',
  },
}

// Days of week
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

// Helper to get days in month
const getDaysInMonth = (year, month) => {
  return new Date(year, month + 1, 0).getDate()
}

// Helper to get first day of month (0 = Sunday)
const getFirstDayOfMonth = (year, month) => {
  return new Date(year, month, 1).getDay()
}

// Stat Card Component
const StatCard = ({ title, value, icon, color, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      p: 2,
      border: '1px solid #e5e7eb',
      borderRadius: 2,
      cursor: onClick ? 'pointer' : 'default',
      transition: 'all 0.2s',
      '&:hover': onClick ? { borderColor: color, boxShadow: 1 } : {},
    }}
  >
    <Box display="flex" alignItems="center" gap={1.5}>
      <Box
        sx={{
          p: 1,
          borderRadius: 1.5,
          backgroundColor: `${color}15`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography variant="h5" fontWeight={700} color={color}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {title}
        </Typography>
      </Box>
    </Box>
  </Paper>
)

// Calendar Day Cell
const CalendarDayCell = ({ date, events, isToday, isCurrentMonth, onDayClick }) => {
  const hasEvents = events && events.length > 0
  const overdueCount = events?.filter((e) => e.is_overdue).length || 0
  const woCount = events?.filter((e) => e.type === 'work_order').length || 0
  const pmCount = events?.filter((e) => e.type === 'pm').length || 0
  const calCount = events?.filter((e) => e.type === 'calibration').length || 0

  return (
    <Paper
      elevation={0}
      onClick={() => onDayClick?.(date, events)}
      sx={{
        height: 90,
        border: '1px solid',
        borderColor: isToday ? 'primary.main' : '#e5e7eb',
        backgroundColor: isCurrentMonth
          ? isToday
            ? 'primary.50'
            : '#fff'
          : '#f9fafb',
        cursor: hasEvents ? 'pointer' : 'default',
        transition: 'all 0.2s',
        '&:hover': hasEvents
          ? {
              borderColor: 'primary.main',
              boxShadow: 1,
            }
          : {},
        overflow: 'hidden',
      }}
    >
      <Box p={0.5}>
        {/* Date number */}
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography
            variant="body2"
            sx={{
              fontWeight: isToday ? 700 : 400,
              color: isCurrentMonth
                ? isToday
                  ? 'white'
                  : 'text.primary'
                : 'text.disabled',
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: isToday ? 'primary.main' : 'transparent',
            }}
          >
            {date.getDate()}
          </Typography>

          {/* Overdue indicator */}
          {overdueCount > 0 && (
            <Tooltip title={`${overdueCount} overdue`}>
              <Badge
                badgeContent={overdueCount}
                color="error"
                max={9}
                sx={{
                  '& .MuiBadge-badge': {
                    fontSize: '0.6rem',
                    minWidth: 14,
                    height: 14,
                  },
                }}
              >
                <Warning sx={{ fontSize: 14, color: 'error.main' }} />
              </Badge>
            </Tooltip>
          )}
        </Box>

        {/* Event indicators */}
        {hasEvents && (
          <Box mt={0.5} display="flex" flexWrap="wrap" gap={0.5}>
            {woCount > 0 && (
              <Tooltip title={`${woCount} Work Order(s)`}>
                <Chip
                  size="small"
                  icon={<Build sx={{ fontSize: '12px !important' }} />}
                  label={woCount}
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    backgroundColor: '#2196F320',
                    color: '#2196F3',
                    '& .MuiChip-icon': { color: '#2196F3' },
                  }}
                />
              </Tooltip>
            )}
            {pmCount > 0 && (
              <Tooltip title={`${pmCount} PM(s)`}>
                <Chip
                  size="small"
                  icon={<EventRepeat sx={{ fontSize: '12px !important' }} />}
                  label={pmCount}
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    backgroundColor: '#3F51B520',
                    color: '#3F51B5',
                    '& .MuiChip-icon': { color: '#3F51B5' },
                  }}
                />
              </Tooltip>
            )}
            {calCount > 0 && (
              <Tooltip title={`${calCount} Calibration(s)`}>
                <Chip
                  size="small"
                  icon={<Speed sx={{ fontSize: '12px !important' }} />}
                  label={calCount}
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    backgroundColor: '#9C27B020',
                    color: '#9C27B0',
                    '& .MuiChip-icon': { color: '#9C27B0' },
                  }}
                />
              </Tooltip>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  )
}

// Day Details Panel
const DayDetailsPanel = ({ date, events, onEventClick }) => {
  if (!date) return null

  const sortedEvents = [...(events || [])].sort((a, b) => {
    // Sort by overdue first, then by type
    if (a.is_overdue !== b.is_overdue) return a.is_overdue ? -1 : 1
    return a.type.localeCompare(b.type)
  })

  return (
    <Card title={`Activities for ${formatDate(date)}`}>
      {sortedEvents.length > 0 ? (
        <List dense disablePadding>
          {sortedEvents.map((event, index) => {
            const config = ACTIVITY_CONFIG[event.type]
            return (
              <Box key={event.id}>
                <ListItem
                  sx={{ px: 0, cursor: 'pointer' }}
                  onClick={() => onEventClick(event)}
                >
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Box
                          sx={{
                            p: 0.5,
                            borderRadius: 1,
                            backgroundColor: `${event.color}20`,
                            display: 'flex',
                          }}
                        >
                          {config.icon}
                        </Box>
                        <Typography variant="body2" fontWeight={500} noWrap>
                          {event.title}
                        </Typography>
                        {event.is_overdue && (
                          <Chip
                            label="Overdue"
                            size="small"
                            color="error"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box mt={0.5}>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          {event.asset_name}
                        </Typography>
                        <Typography
                          variant="caption"
                          display="block"
                          color="text.secondary"
                        >
                          <Person
                            sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.5 }}
                          />
                          {event.assigned_to}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
                {index < sortedEvents.length - 1 && <Divider />}
              </Box>
            )
          })}
        </List>
      ) : (
        <Box py={2} textAlign="center">
          <Typography color="text.secondary">No activities scheduled</Typography>
        </Box>
      )}
    </Card>
  )
}

const CalendarPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [currentDate, setCurrentDate] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState(null)
  const [selectedDayEvents, setSelectedDayEvents] = useState([])
  const [events, setEvents] = useState([])
  const [summary, setSummary] = useState({})
  const [loading, setLoading] = useState(true)

  // Filters
  const [viewType, setViewType] = useState('my') // 'my' or 'all'
  const [selectedTypes, setSelectedTypes] = useState(['work_orders', 'pm', 'calibrations'])

  // Fetch calendar events
  const fetchCalendarEvents = async () => {
    try {
      setLoading(true)
      const year = currentDate.getFullYear()
      const month = currentDate.getMonth()

      // Get first and last day of the month (with buffer for calendar display)
      const startDate = new Date(year, month, 1)
      startDate.setDate(startDate.getDate() - startDate.getDay()) // Start from Sunday

      const endDate = new Date(year, month + 1, 0)
      endDate.setDate(endDate.getDate() + (6 - endDate.getDay())) // End on Saturday

      const response = await api.get('/maintenance/calendar/events/', {
        params: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0],
          view: viewType,
          types: selectedTypes.join(','),
        },
      })

      setEvents(response.data.events || [])
      setSummary(response.data.summary || {})
    } catch (error) {
      console.error('Error fetching calendar events:', error)
      toast.error('Failed to load calendar events')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCalendarEvents()
  }, [currentDate, viewType, selectedTypes])

  // Get calendar grid data
  const calendarGrid = useMemo(() => {
    const year = currentDate.getFullYear()
    const month = currentDate.getMonth()
    const daysInMonth = getDaysInMonth(year, month)
    const firstDay = getFirstDayOfMonth(year, month)

    const grid = []
    let dayCount = 1
    let nextMonthDay = 1

    // Previous month days
    const daysInPrevMonth = getDaysInMonth(year, month - 1)
    const prevMonthDays = firstDay

    for (let week = 0; week < 6; week++) {
      const weekDays = []

      for (let day = 0; day < 7; day++) {
        const cellIndex = week * 7 + day

        if (cellIndex < prevMonthDays) {
          // Previous month
          const prevDate = new Date(
            year,
            month - 1,
            daysInPrevMonth - prevMonthDays + cellIndex + 1
          )
          weekDays.push({ date: prevDate, isCurrentMonth: false })
        } else if (dayCount <= daysInMonth) {
          // Current month
          const currDate = new Date(year, month, dayCount)
          weekDays.push({ date: currDate, isCurrentMonth: true })
          dayCount++
        } else {
          // Next month
          const nextDate = new Date(year, month + 1, nextMonthDay)
          weekDays.push({ date: nextDate, isCurrentMonth: false })
          nextMonthDay++
        }
      }

      grid.push(weekDays)

      // Stop if we've filled the month
      if (dayCount > daysInMonth && week >= 4) break
    }

    return grid
  }, [currentDate])

  // Map events to dates
  const eventsByDate = useMemo(() => {
    const map = {}
    events.forEach((event) => {
      const eventDate = new Date(event.start)
      const dateKey = eventDate.toDateString()
      if (!map[dateKey]) {
        map[dateKey] = []
      }
      map[dateKey].push(event)
    })
    return map
  }, [events])

  const handlePrevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    )
    setSelectedDate(null)
    setSelectedDayEvents([])
  }

  const handleNextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    )
    setSelectedDate(null)
    setSelectedDayEvents([])
  }

  const handleToday = () => {
    setCurrentDate(new Date())
    setSelectedDate(null)
    setSelectedDayEvents([])
  }

  const handleDayClick = (date, events) => {
    setSelectedDate(date)
    setSelectedDayEvents(events || [])
  }

  const handleEventClick = (event) => {
    // Navigate to the appropriate detail page
    if (event.link) {
      navigate(event.link)
    }
  }

  const handleTypeToggle = (type) => {
    setSelectedTypes((prev) => {
      if (prev.includes(type)) {
        return prev.filter((t) => t !== type)
      }
      return [...prev, type]
    })
  }

  const today = new Date()

  return (
    <Box>
      <PageHeader
        title="Calendar"
        subtitle="View your scheduled maintenance activities"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Calendar' },
        ]}
        actions={
          <Box display="flex" gap={2} alignItems="center">
            <ToggleButtonGroup
              value={viewType}
              exclusive
              onChange={(_, value) => value && setViewType(value)}
              size="small"
            >
              <ToggleButton value="my">My Tasks</ToggleButton>
              <ToggleButton value="all">All Tasks</ToggleButton>
            </ToggleButtonGroup>
          </Box>
        }
      />

      {/* Summary Stats */}
      <Grid container spacing={2} mb={3}>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Total Activities"
            value={summary.total || 0}
            icon={<Schedule sx={{ color: '#1B5E7A' }} />}
            color="#1B5E7A"
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Work Orders"
            value={summary.work_orders || 0}
            icon={<Build sx={{ color: '#2196F3' }} />}
            color="#2196F3"
            onClick={() => handleTypeToggle('work_orders')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="PM Schedules"
            value={summary.pm || 0}
            icon={<EventRepeat sx={{ color: '#3F51B5' }} />}
            color="#3F51B5"
            onClick={() => handleTypeToggle('pm')}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <StatCard
            title="Overdue"
            value={summary.overdue || 0}
            icon={<Warning sx={{ color: '#F44336' }} />}
            color="#F44336"
          />
        </Grid>
      </Grid>

      {/* Type Filters */}
      <Box mb={2} display="flex" gap={1} alignItems="center">
        <FilterList sx={{ color: 'text.secondary' }} />
        <Typography variant="body2" color="text.secondary" mr={1}>
          Show:
        </Typography>
        {Object.entries(ACTIVITY_CONFIG).map(([type, config]) => (
          <Chip
            key={type}
            icon={config.icon}
            label={config.label}
            onClick={() => handleTypeToggle(type === 'work_order' ? 'work_orders' : type === 'calibration' ? 'calibrations' : type)}
            variant={
              selectedTypes.includes(type === 'work_order' ? 'work_orders' : type === 'calibration' ? 'calibrations' : type)
                ? 'filled'
                : 'outlined'
            }
            sx={{
              backgroundColor: selectedTypes.includes(
                type === 'work_order' ? 'work_orders' : type === 'calibration' ? 'calibrations' : type
              )
                ? `${config.color}20`
                : 'transparent',
              borderColor: config.color,
              color: config.color,
              '& .MuiChip-icon': { color: config.color },
            }}
          />
        ))}
      </Box>

      <Grid container spacing={3}>
        {/* Calendar Grid */}
        <Grid item xs={12} md={9}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            {/* Calendar Header */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={2}
            >
              <Box display="flex" alignItems="center" gap={1}>
                <IconButton size="small" onClick={handlePrevMonth}>
                  <ChevronLeft />
                </IconButton>
                <IconButton size="small" onClick={handleToday} title="Go to Today">
                  <Today />
                </IconButton>
                <IconButton size="small" onClick={handleNextMonth}>
                  <ChevronRight />
                </IconButton>
              </Box>

              <Typography variant="h6" fontWeight={600}>
                {currentDate.toLocaleDateString('en-US', {
                  month: 'long',
                  year: 'numeric',
                })}
              </Typography>

              <Box minWidth={100} />
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" py={10}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {/* Day headers */}
                <Grid container spacing={0.5} mb={0.5}>
                  {DAYS_OF_WEEK.map((day) => (
                    <Grid item xs key={day} sx={{ textAlign: 'center' }}>
                      <Typography
                        variant="caption"
                        fontWeight={600}
                        color={
                          day === 'Sun' || day === 'Sat'
                            ? 'text.secondary'
                            : 'text.primary'
                        }
                      >
                        {day}
                      </Typography>
                    </Grid>
                  ))}
                </Grid>

                {/* Calendar grid */}
                {calendarGrid.map((week, weekIndex) => (
                  <Grid container spacing={0.5} key={weekIndex} mb={0.5}>
                    {week.map((dayInfo, dayIndex) => (
                      <Grid item xs key={dayIndex}>
                        <CalendarDayCell
                          date={dayInfo.date}
                          events={eventsByDate[dayInfo.date.toDateString()]}
                          isToday={
                            dayInfo.date.toDateString() === today.toDateString()
                          }
                          isCurrentMonth={dayInfo.isCurrentMonth}
                          onDayClick={handleDayClick}
                        />
                      </Grid>
                    ))}
                  </Grid>
                ))}
              </>
            )}
          </Paper>
        </Grid>

        {/* Side Panel */}
        <Grid item xs={12} md={3}>
          <Box display="flex" flexDirection="column" gap={2}>
            {/* Legend */}
            <Card title="Legend">
              <Box display="flex" flexDirection="column" gap={1}>
                {Object.entries(ACTIVITY_CONFIG).map(([type, config]) => (
                  <Box key={type} display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        p: 0.5,
                        borderRadius: 1,
                        backgroundColor: `${config.color}20`,
                        display: 'flex',
                      }}
                    >
                      {config.icon}
                    </Box>
                    <Typography variant="body2" sx={{ color: config.color }}>
                      {config.label}
                    </Typography>
                  </Box>
                ))}
                <Divider sx={{ my: 1 }} />
                <Box display="flex" alignItems="center" gap={1}>
                  <Warning sx={{ fontSize: 16, color: 'error.main' }} />
                  <Typography variant="body2" color="error.main">
                    Overdue
                  </Typography>
                </Box>
              </Box>
            </Card>

            {/* Selected Day Details */}
            {selectedDate && (
              <DayDetailsPanel
                date={selectedDate}
                events={selectedDayEvents}
                onEventClick={handleEventClick}
              />
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  )
}

export default CalendarPage
